import moment from 'moment'
import { notify } from 'reapop'
import { useEffect, useMemo, useState, useCallback } from 'react'
import { DatePicker, Input, Select, Tag, Modal, InputNumber, message } from 'antd'
import { default as CustomButton } from 'components/Button'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import useClient from 'hooks/useClient'
import { DATE_TIME_FORMAT, ICONS, PERMISSIONS } from 'utils/constants'
import SortableColumnTitle from 'components/SortableColumnTitle'
import PageTable from 'components/PageTable'
import Icon from 'components/Icon'
import {
  getLoyaltyPointsList,
  getUserLoyaltyBalance,
  awardLoyaltyPoints,
  adjustLoyaltyPoints,
  expireLoyaltyPoints,
  exportLoyaltyPoints,
} from 'api/loyalty'

const { RangePicker } = DatePicker
const { Option } = Select
const { Search } = Input
const { TextArea } = Input

const LOYALTY_STATUS_COLORS = {
  ACTIVE: 'green',
  REDEEMED: 'blue',
  EXPIRED: 'red',
}

const LOYALTY_SOURCE_LABELS = {
  ORDER: 'Order',
  REFERRAL: 'Referral',
  PROFILE_COMPLETE: 'Profile Completion',
  ADMIN_ADJUSTMENT: 'Admin Adjustment',
}

const LoyaltyPoints = () => {
  const { t } = useTranslation()
  const { isClientCan } = useClient()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [sortType, setSortType] = useState('createdAt')
  const [sortOrder, setSortOrder] = useState('descend')
  const [keySearch, setKeySearch] = useState('')
  const [statusFilter, setStatusFilter] = useState(null)
  const [sourceFilter, setSourceFilter] = useState(null)
  const [dateRangeFilter, setDateRangeFilter] = useState(null)
  
  const [isAwardModalVisible, setIsAwardModalVisible] = useState(false)
  const [isAdjustModalVisible, setIsAdjustModalVisible] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [awardForm, setAwardForm] = useState({ points: 0, reason: '' })
  const [adjustForm, setAdjustForm] = useState({ points: 0, reason: '' })

  const isClientCanManageLoyalty = isClientCan(PERMISSIONS.UPDATE_CONFIGURATIONS)

  const filters = useMemo(() => {
    const f = {}
    if (statusFilter) f.status = statusFilter
    if (sourceFilter) f.source = sourceFilter
    if (dateRangeFilter) {
      f.startDate = dateRangeFilter[0].format('YYYY-MM-DD')
      f.endDate = dateRangeFilter[1].format('YYYY-MM-DD')
    }
    return f
  }, [statusFilter, sourceFilter, dateRangeFilter])

  const {
    data: loyaltyPointsData,
    isFetching,
    refetch,
  } = useQuery(
    ['loyalty-points', currentPage, pageSize, keySearch, sortType, sortOrder, filters],
    async () =>
      await getLoyaltyPointsList({
        page: currentPage,
        pageSize,
        search: keySearch,
        sortBy: sortType,
        sortOrder: sortOrder === 'descend' ? 'DESC' : 'ASC',
        ...filters,
      }),
    {
      retry: 1,
      keepPreviousData: true,
      onError: () => {
        dispatch(
          notify({
            title: t('loyalty.messages.loadFailed'),
            status: 'error',
            dismissAfter: 4000,
          }),
        )
      },
    },
  )

  const awardPointsMutation = useMutation(awardLoyaltyPoints, {
    onSuccess: () => {
      dispatch(
        notify({
          title: t('loyalty.messages.pointsAwarded'),
          status: 'success',
          dismissAfter: 2000,
        }),
      )
      setIsAwardModalVisible(false)
      setAwardForm({ points: 0, reason: '' })
      refetch()
    },
    onError: () => {
      dispatch(
        notify({
          title: t('loyalty.messages.awardFailed'),
          status: 'error',
          dismissAfter: 4000,
        }),
      )
    },
  })

  const adjustPointsMutation = useMutation(adjustLoyaltyPoints, {
    onSuccess: () => {
      dispatch(
        notify({
          title: t('loyalty.messages.pointsAdjusted'),
          status: 'success',
          dismissAfter: 2000,
        }),
      )
      setIsAdjustModalVisible(false)
      setAdjustForm({ points: 0, reason: '' })
      refetch()
    },
    onError: () => {
      dispatch(
        notify({
          title: t('loyalty.messages.adjustFailed'),
          status: 'error',
          dismissAfter: 4000,
        }),
      )
    },
  })

  const handleExport = useCallback(async () => {
    try {
      const blob = await exportLoyaltyPoints({
        search: keySearch,
        ...filters,
      })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `loyalty-points-${moment().format('YYYY-MM-DD')}.xlsx`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      message.success(t('loyalty.messages.exportSuccess'))
    } catch (error) {
      message.error(t('loyalty.messages.exportFailed'))
    }
  }, [keySearch, filters, t])

  const handleAwardPoints = () => {
    if (!selectedUserId || awardForm.points <= 0 || !awardForm.reason) {
      message.warning(t('loyalty.messages.fillAllFields'))
      return
    }
    awardPointsMutation.mutate({
      userId: selectedUserId,
      points: awardForm.points,
      reason: awardForm.reason,
    })
  }

  const handleAdjustPoints = () => {
    if (!selectedUserId || adjustForm.points === 0 || !adjustForm.reason) {
      message.warning(t('loyalty.messages.fillAllFields'))
      return
    }
    adjustPointsMutation.mutate({
      userId: selectedUserId,
      points: adjustForm.points,
      reason: adjustForm.reason,
    })
  }

  const columns = useMemo(() => {
    return [
      {
        title: '№',
        dataIndex: 'id',
        key: 'id',
        align: 'center',
        width: '50px',
        render: (_, record, index) => (currentPage - 1) * pageSize + index + 1,
      },
      {
        title: (props) => (
          <SortableColumnTitle {...props} title={t('loyalty.table.user')} id="userId" />
        ),
        dataIndex: ['user', 'fullName'],
        key: 'userId',
        sorter: true,
        render: (fullName, record) => (
          <div>
            <div>{fullName || '-'}</div>
            <div style={{ fontSize: '12px', color: '#888' }}>
              {record.user?.phoneNumber || '-'}
            </div>
          </div>
        ),
      },
      {
        title: (props) => (
          <SortableColumnTitle {...props} title={t('loyalty.table.points')} id="points" />
        ),
        dataIndex: 'points',
        key: 'points',
        sorter: true,
        align: 'center',
        width: '100px',
        render: (points) => <strong>{points}</strong>,
      },
      {
        title: (props) => (
          <SortableColumnTitle {...props} title={t('loyalty.table.source')} id="source" />
        ),
        dataIndex: 'source',
        key: 'source',
        sorter: true,
        width: '150px',
        render: (source) => LOYALTY_SOURCE_LABELS[source] || source,
      },
      {
        title: t('loyalty.table.sourceId'),
        dataIndex: 'sourceId',
        key: 'sourceId',
        width: '100px',
        render: (sourceId) => sourceId || '-',
      },
      {
        title: t('loyalty.table.status'),
        dataIndex: 'status',
        key: 'status',
        width: '100px',
        render: (status) => (
          <Tag color={LOYALTY_STATUS_COLORS[status]}>{status}</Tag>
        ),
      },
      {
        title: (props) => (
          <SortableColumnTitle {...props} title={t('loyalty.table.expiryDate')} id="expiryDate" />
        ),
        dataIndex: 'expiryDate',
        key: 'expiryDate',
        sorter: true,
        width: '150px',
        render: (expiryDate) =>
          expiryDate ? moment(expiryDate).format(DATE_TIME_FORMAT) : '-',
      },
      {
        title: (props) => (
          <SortableColumnTitle {...props} title={t('loyalty.table.createdAt')} id="createdAt" />
        ),
        dataIndex: 'createdAt',
        key: 'createdAt',
        sorter: true,
        width: '150px',
        render: (createdAt) =>
          createdAt ? moment(createdAt).format(DATE_TIME_FORMAT) : '-',
      },
      {
        title: t('loyalty.table.reason'),
        dataIndex: 'reason',
        key: 'reason',
        width: '200px',
        render: (reason) => reason || '-',
      },
    ]
  }, [t, currentPage, pageSize])

  const tableChangeHandler = (pagination, filter, sorter) => {
    setCurrentPage(pageSize === pagination.pageSize ? pagination.current : 1)
    setPageSize(pagination.pageSize)
    setSortType(sorter.columnKey || null)
    setSortOrder(sorter.order)
  }

  const handleKeySearch = (e) => {
    setKeySearch(e.target.value)
    setCurrentPage(1)
  }

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value)
    setCurrentPage(1)
  }

  const handleSourceFilterChange = (value) => {
    setSourceFilter(value)
    setCurrentPage(1)
  }

  const handleDateRangeChange = (dates) => {
    setDateRangeFilter(dates)
    setCurrentPage(1)
  }

  const filterSection = (
    <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
      <Select
        placeholder={t('loyalty.filters.status')}
        style={{ width: 150 }}
        value={statusFilter}
        onChange={handleStatusFilterChange}
        allowClear
      >
        <Option value="ACTIVE">Active</Option>
        <Option value="REDEEMED">Redeemed</Option>
        <Option value="EXPIRED">Expired</Option>
      </Select>

      <Select
        placeholder={t('loyalty.filters.source')}
        style={{ width: 180 }}
        value={sourceFilter}
        onChange={handleSourceFilterChange}
        allowClear
      >
        <Option value="ORDER">Order</Option>
        <Option value="REFERRAL">Referral</Option>
        <Option value="PROFILE_COMPLETE">Profile Completion</Option>
        <Option value="ADMIN_ADJUSTMENT">Admin Adjustment</Option>
      </Select>

      <RangePicker
        format={DATE_TIME_FORMAT}
        value={dateRangeFilter}
        onChange={handleDateRangeChange}
      />

      <Search
        placeholder={t('loyalty.filters.searchUser')}
        style={{ width: 250 }}
        value={keySearch}
        onChange={handleKeySearch}
        allowClear
      />

      {isClientCanManageLoyalty && (
        <CustomButton type="primary" onClick={handleExport}>
          <Icon icon={ICONS.DOWNLOAD} className="btn-icon-suffix" />
          <span className="btn-text">{t('loyalty.actions.export')}</span>
        </CustomButton>
      )}
    </div>
  )

  return (
    <div>
      <PageTable
        action={filterSection}
        baseData={loyaltyPointsData?.result || []}
        columns={columns}
        loading={isFetching}
        onChange={tableChangeHandler}
        pageTitle={t('loyalty.title')}
        pagination={{
          pageSize,
          current: currentPage,
          total: loyaltyPointsData?.total || 0,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          showSizeChanger: true,
        }}
      />

      {/* Award Points Modal */}
      <Modal
        title={t('loyalty.actions.awardPoints')}
        visible={isAwardModalVisible}
        onOk={handleAwardPoints}
        onCancel={() => {
          setIsAwardModalVisible(false)
          setAwardForm({ points: 0, reason: '' })
        }}
        confirmLoading={awardPointsMutation.isLoading}
      >
        <div style={{ marginBottom: 16 }}>
          <label>{t('loyalty.fields.points')}</label>
          <InputNumber
            min={1}
            style={{ width: '100%' }}
            value={awardForm.points}
            onChange={(value) => setAwardForm({ ...awardForm, points: value })}
          />
        </div>
        <div>
          <label>{t('loyalty.fields.reason')}</label>
          <TextArea
            rows={3}
            value={awardForm.reason}
            onChange={(e) => setAwardForm({ ...awardForm, reason: e.target.value })}
            placeholder={t('loyalty.placeholders.reason')}
          />
        </div>
      </Modal>

      {/* Adjust Points Modal */}
      <Modal
        title={t('loyalty.actions.adjustPoints')}
        visible={isAdjustModalVisible}
        onOk={handleAdjustPoints}
        onCancel={() => {
          setIsAdjustModalVisible(false)
          setAdjustForm({ points: 0, reason: '' })
        }}
        confirmLoading={adjustPointsMutation.isLoading}
      >
        <div style={{ marginBottom: 16 }}>
          <label>{t('loyalty.fields.adjustmentPoints')}</label>
          <InputNumber
            style={{ width: '100%' }}
            value={adjustForm.points}
            onChange={(value) => setAdjustForm({ ...adjustForm, points: value })}
            placeholder={t('loyalty.placeholders.adjustmentPoints')}
          />
        </div>
        <div>
          <label>{t('loyalty.fields.reason')}</label>
          <TextArea
            rows={3}
            value={adjustForm.reason}
            onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })}
            placeholder={t('loyalty.placeholders.reason')}
          />
        </div>
      </Modal>
    </div>
  )
}

export default LoyaltyPoints
